'use client'

import React, { createContext, useContext, useEffect, useState } from 'react'
import { api } from '@/lib/api'

interface AuthUser {
  id: string
  name: string
  email: string
  role: 'ADMIN' | 'SALES' | 'OPS' | 'INSTALLER'
  canCreateInvestmentOrders: boolean
  paymentAuthLevel: number
}

interface AuthContextType {
  user: AuthUser | null
  isLoading: boolean
  login: (email: string, password: string) => Promise<void>
  logout: () => void
}

const AuthContext = createContext<AuthContextType | null>(null)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const stored = localStorage.getItem('user')
    const token = localStorage.getItem('accessToken')
    if (stored && token) {
      setUser(JSON.parse(stored))
      // Refresh from DB to pick up any profile changes (e.g. paymentAuthLevel)
      api.get('/auth/me').then((r) => {
        const fresh = r.data.data as AuthUser
        setUser(fresh)
        localStorage.setItem('user', JSON.stringify(fresh))
      }).catch(() => {})
    }
    setIsLoading(false)
  }, [])

  async function login(email: string, password: string) {
    const res = await api.post('/auth/login', { email, password })
    const { accessToken, refreshToken, user } = res.data.data
    localStorage.setItem('accessToken', accessToken)
    localStorage.setItem('refreshToken', refreshToken)
    localStorage.setItem('user', JSON.stringify(user))
    setUser(user)
  }

  function logout() {
    localStorage.clear()
    document.cookie = 'accessToken=; path=/; max-age=0'
    document.cookie = 'userRole=; path=/; max-age=0'
    setUser(null)
    window.location.href = '/login?signed_out=1'
  }

  return (
    <AuthContext.Provider value={{ user, isLoading, login, logout }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
